import { useState, useEffect, useMemo, useCallback, forwardRef } from 'react';
import MuiAutocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Popper from '@mui/material/Popper';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import throttle from 'lodash/throttle';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { styled } from '@mui/material/styles';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { trackAutocompleteSearchAction } from '@Components/Utilities/Tracking';
import translator from '@Components/Utilities/Translator';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { setSearchHistoryCookie, getSearchHistoryValues } from '@Components/Utilities/CookieHelper';
import ListSubheader from '@mui/material/ListSubheader';

const Autocomplete = forwardRef((props, ref) => {

    const [history, setHistory] = useState();
    const SearchIconButton = styled(IconButton)(({ theme }) => ({
        color: theme.palette.common.white,
        backgroundColor: theme.palette.common.black,
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
        }
    }));

    const quickFindRegex = /^([a-z]{2})(\d{2})([a-z]{2})$/i;
    const defaultFilterOptions = createFilterOptions();
    const routes = useRecoilValue(atoms.routes);
    const onFilterOptions = (options, state) => {
        if (options?.some(op => op.isParent)) {
            let filteredOps = defaultFilterOptions(options, state);
            let valsToKeep = [];
            for (var i = 0; i < filteredOps.length; i++) {
                if (filteredOps[i].parentValue) {
                    valsToKeep.push(filteredOps[i].parentValue);
                }
                valsToKeep.push(filteredOps[i].value);
            }
            return options.filter(op => valsToKeep.includes(op.value));
        }
        else {
            return defaultFilterOptions(options, state);
        }
    };


    const ajaxOptions = {
        enabled: true,
        url: routes.ListingsAutocomplete,
        method: 'get',
        additionalData: null,
        dataKey: 'keywords',
        minLength: 3,
        onSearch: (active, searchValue, searchType) => {
            if (searchType != 'Part') {
                setIsLoading(true);

                let url = ajaxOptions.url;
                if (ajaxOptions.method == 'get') {
                    url = buildUrl(url, ajaxOptions.dataKey, searchValue);

                    if (searchType == 'Attachment') {
                        url += `&SearchType=Attachment`;
                    } else if (searchType == 'Dismantled') {
                        url += `&Condition=Dismantled`;
                    }

                    if (ajaxOptions.additionalData) {
                        for (var key in ajaxOptions.additionalData) {
                            url += `&${key}=${ajaxOptions.additionalData[key]}`;
                        }
                    }
                }

                sandhillsRequest({
                    url: url,
                    method: ajaxOptions.method,
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                }).then((data) => {
                    if (active) {
                        let newOptions = [];

                        if (data) {
                            newOptions = [...newOptions, ...data];
                        }

                        setOptions(newOptions);
                        setIsLoading(false);
                    }
                });
            } else {
                setOptions([]);
            }
        },
        ...props.ajaxOptions
    };

    const searchOptions = {
        enabled: true,
        url: routes.Listings,
        text: translator.translate('sandhills.search'),
        additionalData: null,
        dataKey: 'keywords',
        useSubmitBehavior: false,
        onSearch: null,
        isQuickFindCode: (v) => quickFindRegex.test(v),
        runQuickFindCheck: () => {
            var qfOption = options.find(o => o.TermType == 'QuickFind');
            if (qfOption) {
                window.location.href = qfOption.Url;
            } else {
                sandhillsRequest({
                    url: routes.GetQuickFindUrl,
                    data: {
                        quickFindCode: inputValue
                    },
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                }).then((data) => {
                    if (data) {
                        window.location.href = data;
                    } else {
                        searchOptions.defaultSearch();
                    }
                }).catch(() => {
                    searchOptions.defaultSearch();
                });
            }
        },
        defaultSearch: () => {
            var url = '';
            if (searchType == 'Part') {
                url = buildUrl(props.partsUrl, 'PartNumber', inputValue);
                searchOptions.additionalData = { SearchType: 'Start' };
            }
            else if (searchType == 'Attachment') {
                url = buildUrl(props.attachmentsUrl, searchOptions.dataKey, inputValue);
            }
            else if (searchType == 'Dismantled') {
                url = buildUrl(routes.Dismantled, searchOptions.dataKey, inputValue);
            }
            else {
                url = buildUrl(searchOptions.url, searchOptions.dataKey, inputValue);
            }            
            if (searchOptions.additionalData) {
                for (var key in searchOptions.additionalData) {
                    url += `&${key}=${searchOptions.additionalData[key]}`;
                }
            }
            if (props.showHistory) {
                setSearchHistoryCookie(inputValue, url);
            }

            window.location.href = url;
        },
        runSearch: () => {
            setOpen(false);
            if (searchOptions.useSubmitBehavior) {
                return; // Means this is wrapped in a Form Tag
            } else if (searchOptions.url && !searchOptions.onSearch) {
                setOpenBackdrop(true);
                trackingOptions.save(inputValue);

                if (searchOptions.isQuickFindCode(inputValue)) {
                    searchOptions.runQuickFindCheck();
                } else {
                    searchOptions.defaultSearch();
                }

            } else {
                if (searchOptions.onSearch && typeof searchOptions.onSearch === 'function') {
                    searchOptions.onSearch(inputValue);
                }
            }
            setTimeout(() => {
                setOpenBackdrop(false);
                setIsLoading(false);
            }, 1500);
        },
        ...props.searchOptions
    };

    const trackingOptions = {
        url: routes.SaveAction,
        enabled: true,
        save: (value) => {
            if (trackingOptions.enabled) {
                trackAutocompleteSearchAction(trackingOptions.url, value);
            }
        },
        ...props.trackingOptions
    };

    const styles = {
        bgVariant: null,
        fontWeight: {
            normal: 400,
            highlight: 700
        },
        listbox: {
            maxHeight: 500,
            border: '1px solid #ccc'
        },
        ...props.styles
    };

    const inputOptions = {
        startAdornment: null,
        endAdornment: (value, endAdornment) => {
            const newChildren = React.Children.map(endAdornment?.props?.children || [], c => c);

            if (isLoading) {
                newChildren?.unshift(<CircularProgress color='inherit' size={20} />);
            }

            if (searchOptions.enabled) {
                newChildren?.push(
                    <SearchIconButton key={searchOptions.text} type='submit' onClick={searchOptions.runSearch} aria-label={searchOptions.text} size={size}>
                        <SearchIcon fontSize='small' />
                    </SearchIconButton>
                );
            }

            const c = React.cloneElement(endAdornment ? endAdornment :
                <div
                    style={{
                        position: 'absolute',
                        right: '9px',
                        top: 'calc(50% - 14px)'
                    }}
                />,
                endAdornment?.props,
                newChildren);

            return c;
        },
        ...props.inputOptions
    };

    /* MUI - Popper Props */
    const popperOptions = {
        placement: 'bottom-start',
        disablePortal: false,
        styles: null,

        ...props.popperOptions
    }

    const {
        width,
        textFieldStyles,
        isOptionEqualToValue,
        useAutoSuggestHighlight = true,
        disableClearable,
        disabled,
        /* MUI - Autocomplete Props */
        autoSelect = false,
        clearOnEscape,
        filterOptions = (x) => x,
        freeSolo = true,
        fullWidth = (width != null ? false : true),
        getOptionLabel = (option) => option.value || option,
        //groupBy = (option) => option.TermType,
        id: idProp,
        loadingText = translator.translate('sandhills.loading'),
        onChange = (event, newValue) => {
            if (newValue?.Url) {
                setOpenBackdrop(true);
                trackingOptions.save(newValue);
                if (props.showHistory) {
                    setSearchHistoryCookie(newValue.Label, newValue.Url);
                }

                window.location.href = newValue.Url;
            } else if (event?.key == 'Enter') {
                return searchOptions.runSearch();
            } else {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
            }
        },
        options: optionsProp,
        renderOption = (optionProps, option, { inputValue }) => {
            if (useAutoSuggestHighlight) {
                const matches = match(option.Label, inputValue);
                const parts = parse(option.Label, matches);

                return (
                    <>
                        {props.showHistory && history && inputValue === '' && optionProps['data-option-index'] == 0 &&
                            <li>
                                <ListSubheader component="span">
                                    {translator.translate('sandhills.tradesites.mvc.recentsearches')}
                                </ListSubheader>
                            </li>
                        }
                        {(history || inputValue.length > 0) &&
                            <li {...optionProps}>
                                <div style={{ paddingLeft: option?.Indent > 0 ? `${option.Indent * 10}px` : '' }}>
                                    {parts.map((part, index) => (
                                        <span key={index} style={{ fontWeight: part.highlight ? styles.fontWeight.highlight : styles.fontWeight.normal }}>
                                            {props.showHistory && inputValue === '' && <i class="fa-regular fa-clock media-buttons__icon"></i>}
                                            {part.text}
                                        </span>
                                    ))}
                                </div>
                            </li>
                        }
                    </>
                );
            } else if (option?.isParent) {
                return (
                    <li {...optionProps} {...(option.key ? { key: option.key } : {})}>
                        <b>{getOptionLabel(option)}</b>
                    </li>
                );
            } else {
                return (
                    <li {...optionProps} {...(option.key ? { key: option.key } : {})}>
                        {getOptionLabel(option)}
                    </li>
                );
            }
        },
        size = 'small',
        value: valueProp,

        /* MUI - Textbox Props */
        label = '',
        name: nameProp,
        helperText,
        onBlur,
        variant,

        onOpen
    } = props;    

    const [isOpen, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState(optionsProp || []);
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [searchType, setSearchType] = useState(props.searchType);
    
    const buildUrl = useCallback((urlToBuild, dataKey, searchValue) => {
        let url = urlToBuild;
        if (!url.endsWith('=')) {
            url += url.includes('?') ? '&' : '?';
            url += `${dataKey}=`;
        }
        url += `${encodeURIComponent(searchValue)}`;

        return url;
    }, []);

    const ajaxFetch = useMemo(
        () => throttle((active, inputValue, searchType) => ajaxOptions.onSearch(active, inputValue, searchType), 200),
        [props.ajaxOptions]
    );

    useEffect(() => {
        setSearchType(props.searchType);
    }, [props.searchType]);

    useEffect(() => {
        setOptions(optionsProp || []);
    }, [optionsProp]);

    useEffect(() => {
        setHistory(getSearchHistoryValues());
    }, []);

    useEffect(() => {
        let active = true;

        if (inputValue.length < 1) {
            if (props.showHistory && history) {
                setOptions(history);
            }
            return undefined;
        }

        else if (inputValue.length < ajaxOptions.minLength && ajaxOptions.enabled) {
            setOptions([]);
            return undefined;
        }

        if (ajaxOptions.url && ajaxOptions.enabled) {
            ajaxFetch(active, inputValue, searchType);
        }

        return () => {
            active = false;
        };
    }, [value, inputValue, isOpen, searchType]);

    const getListBoxProps = () => {
        var lbProps = {
            style: {
                maxHeight: styles.listbox.maxHeight,
                border: styles.listbox.border
            }
        };

        return lbProps;
    };

    const onInputChange = (event, newInputValue) => {
        if (event?.key != 'Enter') {
            setInputValue(newInputValue);
        }
    };

    const AutocompletePopper = (popperProps) => {
        return (
            <Popper {...popperProps} disablePortal={ popperOptions.disablePortal}  placement={popperOptions.placement} {...(popperOptions.styles ? { style: popperOptions.styles } : {})}/>
        );
    };

    const renderInput = (params) => {
        const selectedValue = params.inputProps.value;

        return (
            <TextField
                {...params}
                name={nameProp}
                label={label}
                variant={variant}
                bgVariant={styles.bgVariant}
                onBlur={onBlur}
                helperText={helperText}
                error={helperText ? true : false}
                inputRef={ref}
                InputProps={{
                    ...params.InputProps,
                    startAdornment: inputOptions.startAdornment ? inputOptions.startAdornment(selectedValue) : null,
                    endAdornment: inputOptions.endAdornment ? inputOptions.endAdornment(selectedValue, params.InputProps.endAdornment) : null,
                    style: textFieldStyles
                }}
            />
        );
    };

    const onOpenHandler = () => {
        if (onOpen && (!options || options.length <= 0 || (options.length == 1 && options[0].isDefault || options.length == 2 && options[1].isDefault))) {
            setIsLoading(true);
            onOpen?.()?.then(() => {
                setOpen(true);
                setIsLoading(false);
            })?.catch(() => {
                setOpen(true);
                setIsLoading(false);
            });
        }
        else {
            setOpen(true);
        }      
    };

    return (
        <>
            <MuiAutocomplete
                autoSelect={autoSelect}
                clearOnEscape={clearOnEscape}
                filterOptions={freeSolo ? filterOptions : onFilterOptions}
                freeSolo={freeSolo}
                fullWidth={fullWidth}
                getOptionLabel={getOptionLabel}
                //groupBy={groupBy}
                id={idProp}
                ListboxProps={getListBoxProps()}
                loading={isLoading}
                loadingText={loadingText}
                onChange={onChange}
                onClose={(e, r) => {
                    setOpen(false);
                }}
                onInputChange={onInputChange}
                onOpen={onOpenHandler}
                open={isOpen}
                options={options}
                openOnFocus={props.openOnFocus}
                PopperComponent={AutocompletePopper}
                renderInput={renderInput}
                renderOption={renderOption}
                size={size}
                value={valueProp}
                sx={{ width: width }}
                disableClearable={disableClearable}
                disabled={disabled}
                isOptionEqualToValue={isOptionEqualToValue}
            />
            <Backdrop
                open={openBackdrop}
                onClick={() => setOpenBackdrop(false)}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress sx={{ willChange: 'transform', transform: 'translateZ(0)' }} />
            </Backdrop>
        </>
    );
});

export default Autocomplete;